import { PDFDocument } from 'pdf-lib';
import { GenerationError } from '../types';

/**
 * PDF utility functions for page count extraction and metadata operations
 * Uses pdf-lib for robust PDF manipulation without external dependencies
 */
export class PDFUtils {
  /**
   * Extract the actual page count from a PDF buffer
   * @param pdfBuffer - The PDF file as a Buffer
   * @returns The number of pages in the PDF
   * @throws GenerationError if PDF parsing fails
   */
  static async extractPageCount(pdfBuffer: Buffer): Promise<number> {
    try {
      // Load the PDF document from the buffer
      const pdfDoc = await PDFDocument.load(pdfBuffer);

      // Get the number of pages
      const pageCount = pdfDoc.getPageCount();

      return pageCount;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new GenerationError(`Failed to extract page count from PDF: ${errorMessage}`, {
        cause: error as Error,
      });
    }
  }

  /**
   * Extract comprehensive PDF metadata
   * @param pdfBuffer - The PDF file as a Buffer
   * @returns Object containing PDF metadata
   */
  static async extractMetadata(pdfBuffer: Buffer): Promise<{
    pageCount: number;
    pdfVersion: string;
    pageSize?: { width: number; height: number };
  }> {
    try {
      const pdfDoc = await PDFDocument.load(pdfBuffer);

      const pageCount = pdfDoc.getPageCount();

      // Get first page dimensions if available
      let pageSize: { width: number; height: number } | undefined;
      if (pageCount > 0) {
        const firstPage = pdfDoc.getPage(0);
        const { width, height } = firstPage.getSize();
        pageSize = { width, height };
      }

      return {
        pageCount,
        pdfVersion: '1.7', // pdf-lib generates PDF 1.7 by default
        ...(pageSize && { pageSize }),
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new GenerationError(`Failed to extract PDF metadata: ${errorMessage}`, {
        cause: error as Error,
      });
    }
  }

  /**
   * Validate that a buffer contains a valid PDF
   * @param buffer - The buffer to validate
   * @returns true if the buffer contains a valid PDF
   */
  static async isValidPDF(buffer: Buffer): Promise<boolean> {
    try {
      await PDFDocument.load(buffer);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get PDF file size statistics
   * @param pdfBuffer - The PDF file as a Buffer
   * @returns Object containing size statistics in bytes and human-readable format
   */
  static getFileSize(pdfBuffer: Buffer): {
    bytes: number;
    kilobytes: number;
    megabytes: number;
    formatted: string;
  } {
    const bytes = pdfBuffer.length;
    const kilobytes = bytes / 1024;
    const megabytes = kilobytes / 1024;

    let formatted: string;
    if (megabytes >= 1) {
      formatted = `${megabytes.toFixed(2)} MB`;
    } else if (kilobytes >= 1) {
      formatted = `${kilobytes.toFixed(2)} KB`;
    } else {
      formatted = `${bytes} bytes`;
    }

    return {
      bytes,
      kilobytes,
      megabytes,
      formatted,
    };
  }
}
